// src/pages/KarigarManagement/Karigar/KarigarDetailPage.jsx

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useKarigarManagement,
  useJobManagement,
  usePaymentManagement,
} from "@/hooks/useKarigar";
import { Button } from "@/components/ui-v2/button";
import { Input } from "@/components/ui-v2/input";
import { Label } from "@/components/ui-v2/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import { Badge } from "@/components/ui-v2/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui-v2/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui-v2/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui-v2/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui-v2/dialog";
import {
  ArrowLeft,
  Edit,
  Star,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Briefcase,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  Award,
  TrendingUp,
  User,
  Building,
  IndianRupee,
  Key,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  AlertTriangle,
  Shield,
} from "lucide-react";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { toast } from "sonner";
import { format } from "date-fns";

// Status colors
const STATUS_COLORS = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-800",
  pending: "bg-yellow-100 text-yellow-800",
  suspended: "bg-red-100 text-red-800",
  blacklisted: "bg-red-200 text-red-900",
};

const KarigarDetailPage = () => {
  const navigate = useNavigate();
  const { karigarId } = useParams();

  // ========== KARIGAR HOOK ==========
  const {
    currentKarigar,
    loading: karigarLoading,
    error,
    successMessage,
    passwordStatus,
    getKarigarById,
    changeStatus,
    setPassword,
    resetPassword,
    checkPassword,
    clearError,
    clearSuccessMessage,
    clearCurrentKarigar,
  } = useKarigarManagement();

  const { jobs, getJobsByKarigar } = useJobManagement();
  const { payments, getPayments } = usePaymentManagement();

  // ========== STATE ==========
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  // Password management state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordAction, setPasswordAction] = useState(""); // 'set' or 'reset'
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [sendSmsOnReset, setSendSmsOnReset] = useState(true);

  // ========== FETCH DATA ==========
  useEffect(() => {
    if (karigarId) {
      getKarigarById(karigarId);
      checkPassword(karigarId);
      getJobsByKarigar(karigarId, { limit: 10 });
      getPayments({ karigarId, limit: 10 });
    }
    return () => {
      clearCurrentKarigar();
    };
  }, [karigarId]);

  // ========== HANDLE MESSAGES ==========
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      clearSuccessMessage();
    }
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [successMessage, error]);

  // ========== STATUS CHANGE ==========
  const handleStatusChange = async () => {
    await changeStatus(karigarId, newStatus);
    setShowStatusDialog(false);
  };

  // ========== PASSWORD FUNCTIONS ==========

  // Generate random password
  const generateRandomPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let password = "";
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setGeneratedPassword(password);
  };

  // Copy password to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Password copied to clipboard");
  };

  // Open set password dialog
  const openSetPasswordDialog = () => {
    setPasswordAction("set");
    setNewPassword("");
    setGeneratedPassword("");
    setShowPassword(false);
    setShowPasswordDialog(true);
  };

  // Open reset password dialog
  const openResetPasswordDialog = () => {
    setPasswordAction("reset");
    setNewPassword("");
    setGeneratedPassword("");
    setSendSmsOnReset(true);
    setShowPasswordDialog(true);
  };

  // Handle set password
  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setPasswordLoading(true);
    try {
      await setPassword(karigarId, newPassword);
      toast.success("Password set successfully");

      // Refresh password status
      await checkPassword(karigarId);

      // Keep dialog open briefly to allow copying if needed
      if (generatedPassword) {
        toast.info("Make sure to share the password with the karigar securely");
      }

      setShowPasswordDialog(false);
      setNewPassword("");
      setGeneratedPassword("");
    } catch (err) {
      toast.error(err?.message || "Failed to set password");
    } finally {
      setPasswordLoading(false);
    }
  };

  // Handle reset password
  const handleResetPassword = async () => {
    setPasswordLoading(true);
    try {
      const result = await resetPassword(karigarId, sendSmsOnReset);

      // Check if we got a temporary password back (SMS failed or disabled)
      if (result?.payload?.data?.temporaryPassword) {
        setGeneratedPassword(result.payload.data.temporaryPassword);
        toast.success(
          "Password reset successfully. See the temporary password below."
        );
      } else {
        toast.success(
          "Password reset successfully. New password sent via SMS."
        );
        setShowPasswordDialog(false);
      }

      // Refresh password status
      await checkPassword(karigarId);
    } catch (err) {
      toast.error(err?.message || "Failed to reset password");
    } finally {
      setPasswordLoading(false);
    }
  };

  // ========== HELPERS ==========
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // ========== LOADING STATE ==========
  if (karigarLoading.detail) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // ========== NOT FOUND ==========
  if (!currentKarigar) {
    return (
      <div className="container mx-auto py-6">
        <p className="text-center text-muted-foreground">Karigar not found</p>
      </div>
    );
  }

  const karigar = currentKarigar;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* ========== HEADER ========== */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{karigar.name}</h1>
            <p className="text-muted-foreground">{karigar.karigarCode}</p>
          </div>
          <Badge className={STATUS_COLORS[karigar.status] || ""}>
            {karigar.status}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() =>
              navigate(`/karigar-management/karigars/${karigarId}/edit`)
            }
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              navigate(`/karigar-management/karigars/${karigarId}/availability`)
            }
          >
            <Calendar className="h-4 w-4 mr-2" />
            Availability
          </Button>
        </div>
      </div>

      {/* ========== MAIN CONTENT ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ========== LEFT COLUMN - PROFILE INFO ========== */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              {/* Avatar */}
              <div className="h-24 w-24 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <span className="text-3xl font-bold">
                  {karigar.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <h2 className="text-xl font-semibold">{karigar.name}</h2>
              <p className="text-muted-foreground">{karigar.karigarCode}</p>

              {/* Rating */}
              <div className="flex items-center gap-2 mt-4">
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                <span className="text-xl font-bold">
                  {karigar.performance?.overallRating?.toFixed(1) || "0.0"}
                </span>
                <span className="text-muted-foreground">/ 5.0</span>
              </div>

              {/* Contact Info */}
              <div className="w-full mt-6 space-y-3 text-left">
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{karigar.phoneNumber}</span>
                </div>
                {karigar.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{karigar.email}</span>
                  </div>
                )}
                {karigar.address?.city && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {karigar.address.city}, {karigar.address.state}
                    </span>
                  </div>
                )}
              </div>

              {/* ========== PASSWORD MANAGEMENT SECTION ========== */}
              <div className="w-full mt-6 pt-6 border-t">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    App Login Access
                  </p>
                </div>

                {/* Password Status Indicator */}
                <div className="flex items-center gap-2 mb-3 p-3 bg-muted/50 rounded-lg">
                  <Key className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    {karigarLoading.password ? (
                      <span className="text-sm text-muted-foreground">
                        Checking...
                      </span>
                    ) : passwordStatus?.hasPassword ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-green-600 font-medium">
                          Password is set
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                        <span className="text-sm text-yellow-600 font-medium">
                          No password set
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Password Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  {!passwordStatus?.hasPassword ? (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={openSetPasswordDialog}
                      disabled={karigarLoading.password}
                    >
                      <Key className="h-3 w-3 mr-1" />
                      Set Password
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={openSetPasswordDialog}
                        disabled={karigarLoading.password}
                      >
                        <Key className="h-3 w-3 mr-1" />
                        Change Password
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={openResetPasswordDialog}
                        disabled={karigarLoading.password}
                        className="text-orange-600 hover:text-orange-700"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Reset Password
                      </Button>
                    </>
                  )}
                </div>

                {/* Help text */}
                <p className="text-xs text-muted-foreground mt-2">
                  {passwordStatus?.hasPassword
                    ? "Karigar can login to the mobile app with this phone number and password"
                    : "Set a password to allow karigar to login to the mobile app"}
                </p>
              </div>

              {/* ========== STATUS ACTIONS ========== */}
              <div className="w-full mt-6 pt-6 border-t">
                <p className="text-sm text-muted-foreground mb-2">
                  Change Status
                </p>
                <div className="flex flex-wrap gap-2">
                  {karigar.status !== "active" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-600"
                      onClick={() => {
                        setNewStatus("active");
                        setShowStatusDialog(true);
                      }}
                    >
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Activate
                    </Button>
                  )}
                  {karigar.status === "active" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-yellow-600"
                      onClick={() => {
                        setNewStatus("inactive");
                        setShowStatusDialog(true);
                      }}
                    >
                      <XCircle className="h-3 w-3 mr-1" />
                      Deactivate
                    </Button>
                  )}
                  {karigar.status !== "suspended" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600"
                      onClick={() => {
                        setNewStatus("suspended");
                        setShowStatusDialog(true);
                      }}
                    >
                      Suspend
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ========== RIGHT COLUMN - STATS & DETAILS ========== */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <Briefcase className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold">
                  {karigar.performance?.totalJobsCompleted || 0}
                </p>
                <p className="text-xs text-muted-foreground">Jobs Completed</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Clock className="h-6 w-6 mx-auto text-purple-500 mb-2" />
                <p className="text-2xl font-bold">
                  {karigar.workload?.currentJobs || 0}
                </p>
                <p className="text-xs text-muted-foreground">Active Jobs</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <TrendingUp className="h-6 w-6 mx-auto text-green-500 mb-2" />
                <p className="text-2xl font-bold">
                  {karigar.performance?.onTimeDeliveryRate?.toFixed(0) || 0}%
                </p>
                <p className="text-xs text-muted-foreground">On-Time Rate</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <IndianRupee className="h-6 w-6 mx-auto text-orange-500 mb-2" />
                <p className="text-2xl font-bold">
                  {formatCurrency(karigar.financials?.totalEarnings || 0)}
                </p>
                <p className="text-xs text-muted-foreground">Total Earnings</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="skills">Skills</TabsTrigger>
              <TabsTrigger value="jobs">Jobs</TabsTrigger>
              <TabsTrigger value="payments">Payments</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4">
              {/* Branches */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Associated Branches
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {karigar.associatedBranches?.map((branch, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded"
                      >
                        <div>
                          <p className="font-medium">{branch.branchName}</p>
                          <p className="text-sm text-muted-foreground">
                            {branch.branchCode}
                          </p>
                        </div>
                        {branch.isPrimary && <Badge>Primary</Badge>}
                      </div>
                    ))}
                    {(!karigar.associatedBranches ||
                      karigar.associatedBranches.length === 0) && (
                      <p className="text-muted-foreground">
                        No branches assigned
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Work Preferences */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Work Preferences</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Max Concurrent Jobs
                      </p>
                      <p className="font-medium">
                        {karigar.workPreferences?.maxConcurrentJobs || 5}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Preferred Work Types
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {karigar.workPreferences?.preferredWorkTypes?.map(
                          (type, idx) => (
                            <Badge
                              key={idx}
                              variant="outline"
                              className="text-xs"
                            >
                              {type.replace(/_/g, " ")}
                            </Badge>
                          )
                        )}
                        {(!karigar.workPreferences?.preferredWorkTypes ||
                          karigar.workPreferences.preferredWorkTypes.length ===
                            0) && (
                          <span className="text-muted-foreground text-sm">
                            None specified
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bank Details */}
              {karigar.bankDetails?.accountNumber && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Bank Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Account Holder
                        </p>
                        <p className="font-medium">
                          {karigar.bankDetails.accountHolderName || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Account Number
                        </p>
                        <p className="font-medium">
                          {karigar.bankDetails.accountNumber || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Bank Name
                        </p>
                        <p className="font-medium">
                          {karigar.bankDetails.bankName || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          IFSC Code
                        </p>
                        <p className="font-medium">
                          {karigar.bankDetails.ifscCode || "-"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Skills Tab */}
            <TabsContent value="skills">
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {karigar.skills?.map((skill, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded"
                      >
                        <div className="flex items-center gap-3">
                          <Award className="h-5 w-5 text-primary" />
                          <div>
                            <p className="font-medium">
                              {skill.category?.replace(/_/g, " ")}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {skill.experienceYears} years experience
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              skill.proficiencyLevel === "EXPERT" ||
                              skill.proficiencyLevel === "MASTER"
                                ? "default"
                                : "outline"
                            }
                          >
                            {skill.proficiencyLevel}
                          </Badge>
                          {skill.isVerified && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                      </div>
                    ))}
                    {(!karigar.skills || karigar.skills.length === 0) && (
                      <p className="text-center text-muted-foreground py-4">
                        No skills added
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Jobs Tab */}
            <TabsContent value="jobs">
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Due Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {jobs.slice(0, 10).map((job) => (
                        <TableRow
                          key={job._id}
                          className="cursor-pointer"
                          onClick={() =>
                            navigate(`/karigar-management/jobs/${job._id}`)
                          }
                        >
                          <TableCell className="font-medium">
                            {job.jobNumber}
                          </TableCell>
                          <TableCell>
                            {job.jobType?.replace(/_/g, " ")}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{job.status}</Badge>
                          </TableCell>
                          <TableCell>
                            {job.timeline?.expectedCompletionDate
                              ? format(
                                  new Date(job.timeline.expectedCompletionDate),
                                  "dd MMM yyyy"
                                )
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                      {jobs.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            className="text-center text-muted-foreground"
                          >
                            No jobs found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments">
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Payment ID</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.slice(0, 10).map((payment) => (
                        <TableRow
                          key={payment._id}
                          className="cursor-pointer"
                          onClick={() =>
                            navigate(
                              `/karigar-management/payments/${payment._id}`
                            )
                          }
                        >
                          <TableCell className="font-medium">
                            {payment.paymentNumber}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(payment.amount)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{payment.status}</Badge>
                          </TableCell>
                          <TableCell>
                            {payment.createdAt
                              ? format(
                                  new Date(payment.createdAt),
                                  "dd MMM yyyy"
                                )
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                      {payments.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            className="text-center text-muted-foreground"
                          >
                            No payments found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* ========== STATUS CHANGE DIALOG ========== */}
      <AlertDialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Status</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change this karigar's status to "
              {newStatus}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleStatusChange}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ========== PASSWORD MANAGEMENT DIALOG ========== */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {passwordAction === "set" ? (
                <>
                  <Key className="h-5 w-5" />
                  {passwordStatus?.hasPassword
                    ? "Change Password"
                    : "Set Password"}
                </>
              ) : (
                <>
                  <RefreshCw className="h-5 w-5" />
                  Reset Password
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {passwordAction === "set"
                ? `Set a login password for ${karigar?.name} (${karigar?.phoneNumber})`
                : `Reset password for ${karigar?.name}. A new password will be generated.`}
            </DialogDescription>
          </DialogHeader>

          {passwordAction === "set" ? (
            // ========== SET PASSWORD FORM ==========
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter password (min 6 characters)"
                    className="pr-20"
                  />
                  <div className="absolute right-0 top-0 h-full flex items-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-full px-2"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    {newPassword && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-full px-2"
                        onClick={() => copyToClipboard(newPassword)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {newPassword && newPassword.length < 6 && (
                  <p className="text-sm text-red-500">
                    Password must be at least 6 characters
                  </p>
                )}
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={generateRandomPassword}
                className="w-full"
              >
                <RefreshCw className="h-3 w-3 mr-2" />
                Generate Random Password
              </Button>

              {generatedPassword && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 mb-2">
                    Generated password (make sure to copy it):
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-white rounded font-mono text-lg">
                      {generatedPassword}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(generatedPassword)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  Share this password with the karigar securely. They will use
                  it with phone number <strong>
                    {karigar?.phoneNumber}
                  </strong>{" "}
                  to login.
                </p>
              </div>
            </div>
          ) : (
            // ========== RESET PASSWORD FORM ==========
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  This will generate a new random password for the karigar. The
                  old password will no longer work.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sendSms"
                  checked={sendSmsOnReset}
                  onChange={(e) => setSendSmsOnReset(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="sendSms" className="text-sm cursor-pointer">
                  Send new password via SMS to {karigar?.phoneNumber}
                </Label>
              </div>

              {!sendSmsOnReset && (
                <p className="text-sm text-muted-foreground">
                  If SMS is disabled, you'll receive the new password here to
                  share manually.
                </p>
              )}

              {generatedPassword && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 mb-2">
                    New password generated (SMS sending{" "}
                    {sendSmsOnReset ? "failed" : "was disabled"}):
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-white rounded font-mono text-lg">
                      {generatedPassword}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(generatedPassword)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-green-700 mt-2">
                    Please share this password with the karigar securely.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowPasswordDialog(false);
                setNewPassword("");
                setGeneratedPassword("");
              }}
              disabled={passwordLoading}
            >
              {generatedPassword && passwordAction === "reset"
                ? "Close"
                : "Cancel"}
            </Button>
            {!(generatedPassword && passwordAction === "reset") && (
              <Button
                onClick={
                  passwordAction === "set"
                    ? handleSetPassword
                    : handleResetPassword
                }
                disabled={
                  passwordLoading ||
                  (passwordAction === "set" && newPassword.length < 6)
                }
              >
                {passwordLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : passwordAction === "set" ? (
                  "Set Password"
                ) : (
                  "Reset Password"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KarigarDetailPage;
